import { api } from '../../api.js';
import { els, setStatus } from './dom.js';
import { shouldUseImageList } from './rules.js';
import { getCurrentCreditEstimate, getSelectedMode, getSelectedRule, renderGenerationProgress, renderHistory, renderPreview } from './view.js';
import { getVideoSubmitBlockReason } from './readiness.js';

export function createVideoTaskController(state, { refreshSubmitState } = {}) {
  async function uploadLocalReference(item, index, total) {
    if (!item || item.assetId) return null;
    if (/^https?:\/\//i.test(String(item.dataUrl || ''))) return item.dataUrl;
    if (!item.base64) return null;
    const message = `正在上传参考图 ${index + 1}/${total}...`;
    setStatus(message);
    renderGenerationProgress({ status: 'running', phase: 'upload', progressMessage: message });
    const resp = await api.videoUploadReference({
      imageBase64: item.base64,
      imageMimeType: item.mimeType || 'image/jpeg',
      fileName: item.name || `reference-${index + 1}.jpg`,
    });
    return resp?.imageUrl || resp?.url || '';
  }

  async function uploadLastFrameIfNeeded() {
    if (!state.lastFrame?.base64) return '';
    setStatus('正在上传尾帧图...');
    renderGenerationProgress({ status: 'running', phase: 'upload', progressMessage: '正在上传尾帧图...' });
    const resp = await api.videoUploadReference({
      imageBase64: state.lastFrame.base64,
      imageMimeType: state.lastFrame.mimeType || 'image/jpeg',
      fileName: state.lastFrame.name || 'last-frame.jpg',
    });
    return resp?.imageUrl || resp?.url || '';
  }

  async function buildRequestBody() {
    const rule = getSelectedRule();
    const mode = getSelectedMode(state);
    const videoType = els.videoType()?.value || 'SEED_FAST';
    const useList = Boolean(rule.requiresList || shouldUseImageList(videoType, mode));
    const first = state.referenceImages[0];
    const assetIds = state.referenceImages
      .map((item) => item.assetId)
      .filter(Boolean);
    const body = {
      listingId: state.listing?.id || null,
      newProductProjectId: state.newProductProjectId || null,
      inputMode: mode,
      videoType,
      videoTime: Number(els.videoTime()?.value || 10),
      prompt: els.prompt()?.value.trim(),
      promptOptimizer: Boolean(els.optimizer()?.checked),
      resolution: els.resolution()?.value || '',
      aspectRatio: els.ratio()?.value || '',
      voice: Boolean(els.voice()?.checked),
      camera: els.camera()?.value || '',
      estimatedCredits: getCurrentCreditEstimate(state),
      promptSource: state.promptSource || 'manual',
      referenceSource: state.referenceSource || '',
      useImageList: useList,
    };
    const localItems = state.referenceImages.filter((item) => !item.assetId);
    const uploadedLocalUrls = [];
    for (let index = 0; index < localItems.length; index += 1) {
      const url = await uploadLocalReference(localItems[index], index, localItems.length);
      if (url) uploadedLocalUrls.push(url);
    }
    if (assetIds.length) body.imageAssetIds = assetIds;
    if (useList) {
      if (uploadedLocalUrls.length) body.imageList = uploadedLocalUrls;
    } else {
      if (uploadedLocalUrls[0]) {
        body.imageUrl = uploadedLocalUrls[0];
      } else if (!assetIds.length) {
        body.imageBase64 = first.base64;
        body.imageMimeType = first.mimeType;
      }
    }
    if (mode === 'firstLast' && state.lastFrame) {
      body.lastFrameImageUrl = await uploadLastFrameIfNeeded();
      body.voice = false;
    }
    return body;
  }

  async function loadVideos() {
    if (!state.listing?.id) return;
    try {
      const resp = await api.getVideoAssets({ listingId: state.listing.id, includePending: 1 });
      renderHistory(resp?.videos || []);
    } catch (err) {
      renderHistory([]);
      setStatus(err?.message || '加载视频历史失败', 'error');
    }
  }

  function stopPolling() {
    if (state.pollTimer) window.clearTimeout(state.pollTimer);
    state.pollTimer = null;
  }

  async function pollTask(taskId) {
    stopPolling();
    state.pollStartedAt = state.pollStartedAt || Date.now();
    try {
      const resp = await api.videoTaskStatus(taskId);
      const task = resp?.task || resp;
      if (task?.status === 'done' || task?.status === 'completed') {
        state.activeTaskId = null;
        state.previewStale = false;
        setStatus('视频生成完成');
        renderPreview(task);
        await loadVideos();
        refreshSubmitState?.();
        return;
      }
      if (task?.status === 'failed') {
        state.activeTaskId = null;
        state.previewStale = false;
        const message = task.error || task.progressMessage || '视频生成失败，请调整参数后重试。';
        setStatus(message, 'error');
        renderGenerationProgress({ ...task, status: 'failed', phase: 'error', progressMessage: message }, { tone: 'error' });
        await loadVideos();
        refreshSubmitState?.();
        return;
      }
      setStatus(task?.progressMessage || '正在生成视频...');
      renderGenerationProgress(task || { status: 'running', phase: 'poll', progressMessage: '正在生成视频...' });
      const elapsed = Date.now() - (state.pollStartedAt || Date.now());
      const delay = elapsed < 30000 ? 3000 : Math.min(30000, 10000 + Math.floor((elapsed - 30000) / 30000) * 5000);
      state.pollTimer = window.setTimeout(() => pollTask(taskId), delay);
    } catch (err) {
      state.activeTaskId = null;
      state.previewStale = false;
      const message = err?.message || '查询视频任务失败';
      setStatus(message, 'error');
      renderGenerationProgress({ status: 'failed', phase: 'error', progressMessage: message }, { tone: 'error' });
      refreshSubmitState?.();
    }
  }

  async function submitVideo() {
    const blockReason = getVideoSubmitBlockReason(state);
    if (blockReason) {
      setStatus(blockReason, 'error');
      return;
    }
    const btn = els.generateBtn();
    btn.disabled = true;
    btn.textContent = '提交中...';
    try {
      setStatus('正在上传参考图并提交 LinkFox 图转视频任务...');
      renderGenerationProgress({ status: 'running', phase: 'upload', progressMessage: '正在准备参考图并提交视频任务...' });
      const body = await buildRequestBody();
      setStatus('正在提交 LinkFox 图转视频任务...');
      renderGenerationProgress({ status: 'running', phase: 'submit', progressMessage: '正在提交 LinkFox 图转视频任务...' });
      const resp = await api.videoGenerateBackground(body);
      const task = resp?.task || resp;
      state.activeTaskId = task.taskId;
      state.previewStale = false;
      btn.textContent = '视频生成中...';
      renderGenerationProgress(task);
      await loadVideos();
      pollTask(task.taskId);
    } catch (err) {
      state.activeTaskId = null;
      state.previewStale = false;
      const message = err?.message || '提交视频任务失败';
      setStatus(message, 'error');
      renderGenerationProgress({ status: 'failed', phase: 'error', progressMessage: message }, { tone: 'error' });
      refreshSubmitState?.();
    }
  }

  return {
    buildRequestBody,
    loadVideos,
    pollTask,
    stopPolling,
    submitVideo,
  };
}
